import { Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import React from 'react';
import { Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { addDaysToInputDate, istLocalInputToEpoch, istTodayInputDate } from './constants';

export const PAST_RANGES = {
  days7: 'days7',
  days30: 'days30',
  days90: 'days90',
  custom: 'custom',
};

const OPTIONS = [
  { key: PAST_RANGES.days7, value: PAST_RANGES.days7, text: 'Last 7 days' },
  { key: PAST_RANGES.days30, value: PAST_RANGES.days30, text: 'Last 30 days' },
  { key: PAST_RANGES.days90, value: PAST_RANGES.days90, text: 'Last 90 days' },
  { key: PAST_RANGES.custom, value: PAST_RANGES.custom, text: 'Date range' },
];

const PRESET_DAYS = {
  [PAST_RANGES.days7]: 7,
  [PAST_RANGES.days30]: 30,
  [PAST_RANGES.days90]: 90,
};

const Fields = styled(Row)`
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  input[type='date'] {
    padding: 7px 9px;
    border: 1px solid ${COLORS.GRAY_LIGHT};
    border-radius: 4px;
    font-size: 13px;
    font-family: inherit;
  }
`;

const Label = styled(Text)`
  white-space: nowrap;
`;

/** Whole IST days, inclusive of both ends, as the epoch bounds the services filter on. */
export const pastRangeToEpochs = ({ rangeType, startDate, endDate }) => {
  const days = PRESET_DAYS[rangeType];
  const today = istTodayInputDate();

  if (days) {
    return {
      start_epoch: istLocalInputToEpoch(`${addDaysToInputDate(today, -(days - 1))}T00:00`),
      end_epoch: istLocalInputToEpoch(`${today}T23:59`),
    };
  }
  if (!startDate || !endDate) return {};

  return {
    start_epoch: istLocalInputToEpoch(`${startDate}T00:00`),
    end_epoch: istLocalInputToEpoch(`${endDate}T23:59`),
  };
};

export const defaultPastRange = () => ({
  rangeType: PAST_RANGES.days30,
  startDate: '',
  endDate: '',
});

/** A backward-looking window, always IST. */
const PastRangePicker = ({ value, onChange }) => {
  const { rangeType, startDate, endDate } = value;
  const invalid =
    rangeType === PAST_RANGES.custom &&
    startDate &&
    endDate &&
    startDate > endDate;

  return (
    <Fields gutter={0}>
      <Label variant="body3" color={COLORS.GRAY}>
        Showing
      </Label>
      <Dropdown
        compact
        selection
        selectOnBlur={false}
        options={OPTIONS}
        value={rangeType}
        onChange={(_, { value: next }) =>
          onChange(
            next === PAST_RANGES.custom
              ? {
                  rangeType: next,
                  startDate: addDaysToInputDate(istTodayInputDate(), -6),
                  endDate: istTodayInputDate(),
                }
              : { ...value, rangeType: next },
          )
        }
        aria-label="Date range preset"
      />
      {rangeType === PAST_RANGES.custom && (
        <>
          <input
            type="date"
            value={startDate || ''}
            max={endDate || istTodayInputDate()}
            onChange={event => onChange({ ...value, startDate: event.target.value })}
            aria-label="Start date"
          />
          <Text variant="body3" color={COLORS.GRAY}>
            to
          </Text>
          <input
            type="date"
            value={endDate || ''}
            min={startDate || undefined}
            max={istTodayInputDate()}
            onChange={event => onChange({ ...value, endDate: event.target.value })}
            aria-label="End date"
          />
          {invalid && (
            <Text variant="body3" color={COLORS.RUSTY_RED}>
              The end date is before the start.
            </Text>
          )}
        </>
      )}
      <Text variant="body3" color={COLORS.GRAY}>
        IST
      </Text>
    </Fields>
  );
};

export default PastRangePicker;
